import { apiRequest } from '../api';
import { state } from '../state';
import { tierLetter, type PlayerWithStats, type PlayersListResponse } from '../types';

import terranIcon from '../../assets/race/terran.svg';
import zergIcon from '../../assets/race/zerg.svg';
import protossIcon from '../../assets/race/protoss.svg';
import type { Race, StatKey } from '../types';
import { buildRadarSVG, pickRaceColor, STAT_ORDER, type StatAxis } from '../radar';
import randomIcon from '../../assets/race/random.svg';
import { applyTranslations, onLocaleChange, t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { getLocalePlayerName } from '../utils';
import { dominantRace } from '../card';

applyTranslations();

onLocaleChange(() => {
    applyTranslations();
    // if (lastPlayers && lastCb) renderPlayersTable(lastPlayers, lastCb);

    if (lastView && lastViewCb) setView(lastView, lastViewCb);
});

let viewInitialized = false;

// ---- Режим тирлиста: средний / мой / по GHOST / тирлист выбранного игрока ----
type TierListMode = 'average' | 'personal' | 'ghost' | 'user';

const TIERLIST_MODE_STORAGE = 'tierlistMode';
const TIERLIST_RATER_STORAGE = 'tierlistRater';

/** Аккаунт, чей тирлист показываем в режиме «user». */
let tierlistRaterId: string | null = localStorage.getItem(TIERLIST_RATER_STORAGE);

function readTierListMode(): TierListMode {
    const raw = localStorage.getItem(TIERLIST_MODE_STORAGE);
    return raw === 'personal' || raw === 'ghost' || raw === 'user' ? raw : 'average';
}

let tierlistMode: TierListMode = readTierListMode();

/** Пользователь, выставлявший тиры (для выпадающего списка). */
interface TierRater {
    user_id: string;
    username: string;
    player_id: number | null;
    player_name: string | null;
    count: number;
}

let tierRaters: TierRater[] | null = null;
let tierRatersLoading = false;

// Тиры выбранного пользователя (player_id → tier).
let raterTiers: Map<number, number> | null = null;
let raterTiersUserId: string | null = null;
let raterTiersLoadingFor: string | null = null;
let raterTiersError = false;
let raterTiersRequest = 0;

async function ensureTierRaters(): Promise<void> {
    if (tierRaters !== null || tierRatersLoading) return;
    tierRatersLoading = true;
    try {
        const res = await apiRequest<{ raters: TierRater[] }>('/api/players/raters');
        tierRaters = res.raters ?? [];
    } catch {
        tierRaters = [];
    } finally {
        tierRatersLoading = false;
    }
}

async function ensureRaterTiers(userId: string): Promise<void> {
    if (raterTiersUserId === userId && raterTiers) return;
    if (raterTiersLoadingFor === userId) return;

    // Номер запроса: ответ устаревшего запроса не перезапишет новый выбор.
    const request = ++raterTiersRequest;
    raterTiersLoadingFor = userId;
    try {
        const res = await apiRequest<{ tiers: Record<string, number> }>(
            `/api/players/raters/${encodeURIComponent(userId)}/tiers`
        );
        if (request !== raterTiersRequest) return;
        raterTiers = new Map(
            Object.entries(res.tiers ?? {}).map(([id, tier]) => [Number(id), Number(tier)])
        );
        raterTiersError = false;
    } catch {
        if (request !== raterTiersRequest) return;
        raterTiers = new Map();
        raterTiersError = true;
    } finally {
        if (request === raterTiersRequest) {
            raterTiersUserId = userId;
            raterTiersLoadingFor = null;
        }
    }
}

// Тиры, выставленные текущим пользователем (player_id → tier).
// null — ещё не загружали; грузим лениво при выборе режима «Моя».
let myTiers: Map<number, number> | null = null;

async function ensureMyTiers(): Promise<void> {
    if (myTiers !== null || !state.token) return;
    try {
        const res = await apiRequest<{ tiers: Record<string, number> }>(
            '/api/players/my-tiers',
            { token: state.token }
        );
        myTiers = new Map(
            Object.entries(res.tiers ?? {}).map(([id, tier]) => [Number(id), tier])
        );
    } catch {
        myTiers = new Map();
    }
}

/** Эффективный режим: «Моя» без авторизации откатывается к GHOST. */
function effectiveTierListMode(): TierListMode {
    if (tierlistMode === 'personal' && !(state.token && state.user)) return 'ghost';
    if (tierlistMode === 'user' && !tierlistRaterId) return 'ghost';
    return tierlistMode;
}

/** Тир игрока в выбранном режиме тирлиста. */
function tierForPlayer(p: PlayerWithStats, mode: TierListMode): number | null {
    if (mode === 'ghost') return p.ghost_tier ?? null;
    if (mode === 'personal') return myTiers?.get(p.id) ?? null;
    if (mode === 'user') return raterTiers?.get(p.id) ?? null;
    return p.tier ?? null;
}

const RACE_ICON_URL: Record<Race, string> = {
    T: terranIcon,
    Z: zergIcon,
    P: protossIcon,
    R: randomIcon,
};

const RACE_LABELS: Record<Race, string> = {
    T: 'Terran',
    Z: 'Zerg',
    P: 'Protoss',
    R: 'Random',
};

type SortDirection = 'asc' | 'desc';
type SortKey = 'name' | 'total' | 'races' | 'elo' | 'activity' | 'tier' | StatKey;

let sortKey: SortKey = 'activity';
let sortDirection: SortDirection = 'desc';

/**
 * Ключи сортировки, доступные в режиме «карточки» — те же, что и колонки
 * таблицы (совпадают с th[data-sort-key]).
 */
const WALL_SORT_KEYS: SortKey[] = [
    'activity',
    'name',
    'tier',
    'races',
    'adaptiveness',
    'aggression',
    'turtle',
    'variety',
    'survival',
    'greed',
];

function sortKeyLabel(key: SortKey): string {
    if (key === 'name') return t('players.col.name');
    if (key === 'races') return t('players.col.races');
    if (key === 'tier') return t('ratings.col.tier');
    if (key === 'total') return t('players.col.total');
    if (key === 'elo') return t('players.col.elo');
    if (key === 'activity') return t('players.col.activity');
    return t(`stat.${key}` as TranslationKey);
}

/**
 * Хранилище последнего загруженного списка — чтобы пересортировывать
 * без повторного запроса к API.
 */
let cachedPlayers: PlayerWithStats[] = [];

/**
 * Одна ячейка таблицы: полоска заполнения + буква рейтинга.
 * Если значение null — пустая полоска и «—».
 */
export function statBarCell(value: number | null, color: string): HTMLTableCellElement {
    const td = document.createElement('td');
    td.className = 'stat-cell';

    const wrap = document.createElement('div');
    wrap.className = 'stat-bar-wrap';
    wrap.style.setProperty('--stat-color', color);

    const bar = document.createElement('div');
    bar.className = 'stat-bar';

    const fill = document.createElement('div');
    fill.className = 'stat-bar-fill';

    if (typeof value === 'number') {
        const pct = Math.max(0, Math.min(100, ((value - 1) / 4) * 80 + 20));
        fill.style.width = `${pct}%`;
    } else {
        fill.style.width = '0%';
        wrap.classList.add('stat-bar-wrap--empty');
    }

    bar.appendChild(fill);
    wrap.appendChild(bar);
    td.appendChild(wrap);
    return td;
}

interface PlayersCallbacks {
    onOpenPlayer: (id: number, name: string) => void;
}

export async function loadPlayers(cb: PlayersCallbacks): Promise<void> {
    const tbody = document.getElementById('players-tbody');
    const errBox = document.getElementById('players-error');
    if (!tbody || !errBox) return;

    // Список оценщиков и их тиры могли измениться — сбрасываем кэш на каждом входе.
    tierRaters = null;
    raterTiers = null;
    raterTiersUserId = null;
    raterTiersLoadingFor = null;
    raterTiersError = false;
    raterTiersRequest++;

    errBox.classList.add('hidden');
    tbody.innerHTML = `<tr><td colspan="10">${t("common.loading")}</td></tr>`;

    const searchInput = document.getElementById('players-search') as HTMLInputElement | null;

    searchInput?.addEventListener('input', () => {
        searchQuery = searchInput.value.trim().toLowerCase();
        // Перерисовываем текущий вид — таблицу или стену
        setView(currentView, cb);
    });

    try {
        const { players } = await apiRequest<PlayersListResponse>('/api/players');
        cachedPlayers = players;

        // 1. Навешиваем обработчики ОДИН РАЗ
        bindViewToggle(cb);
        bindSorting(cb);
        bindWallSort(cb);

        // 2. Определяем сохранённый вид (если ещё не задан)
        if (!viewInitialized) {
            const saved = localStorage.getItem('playersView');
            if (saved === 'table' || saved === 'wall' || saved === 'tierlist') currentView = saved;
            viewInitialized = true;
        }

        // 3. Применяем вид
        setView(currentView, cb);
    } catch (err) {
        errBox.textContent =
            'Не удалось загрузить игроков: ' +
            (err instanceof Error ? err.message : String(err));
        errBox.classList.remove('hidden');
        tbody.innerHTML = '';
    }
}

function fmtRaceCells(races: PlayerWithStats['races']): HTMLTableCellElement {
    const td = document.createElement('td');
    td.className = 'races-cell';

    if (races.length === 0) {
        td.textContent = '—';
        return td;
    }

    // Внутренняя обёртка — flex-контейнер, а сам td остаётся table-cell
    const inner = document.createElement('div');
    inner.className = 'races-cell-inner';

    for (const r of races) {
        const img = document.createElement('img');
        img.src = RACE_ICON_URL[r];
        img.alt = RACE_LABELS[r];
        img.title = RACE_LABELS[r];
        img.className = `race-icon race-icon-${r}`;
        inner.appendChild(img);
    }

    td.appendChild(inner);
    return td;
}

function renderPlayersTable(
    _players: PlayerWithStats[],
    cb: PlayersCallbacks
): void {
    const tbody = document.getElementById('players-tbody');
    if (!tbody) return;
    tbody.innerHTML = '';

    const filtered = searchQuery
        ? _players.filter((p) =>
            p.name.toLowerCase().includes(searchQuery) ||
            (p.aka ?? '').toLowerCase().includes(searchQuery)
        )
        : _players;

    if (filtered.length === 0) {
        tbody.innerHTML =
            `<tr><td colspan="10" class="hint">${t("players.no_players")}.</td></tr>`;
        return;
    }

    for (const p of filtered) {
        const tr = document.createElement('tr');

        // Если это наш собственный профиль — подсветить
        // Подсветить свою строку
        if (state.user?.player_id && p.id === state.user.player_id) {
            tr.classList.add('is-me');
        }

        // Тир — до имени игрока. В таблице показываем тир по GHOST-оценкам.
        const tdTier = document.createElement('td');
        tdTier.className = 'tier-cell';
        const tierValue = p.ghost_tier ?? null;
        const tierLtr = tierLetter(tierValue ?? null);
        if (tierLtr) {
            const badge = document.createElement('span');
            badge.className = 'tier-badge';
            badge.dataset.tier = String(Math.max(1, Math.min(7, Math.round(tierValue as number))));
            badge.textContent = tierLtr;
            badge.title = t('ratings.col.tier');
            tdTier.appendChild(badge);
        } else {
            const empty = document.createElement('span');
            empty.className = 'tier-badge tier-badge--empty';
            empty.textContent = '—';
            tdTier.appendChild(empty);
        }
        tr.appendChild(tdTier);

        // Имя (текст, не ссылка — кликабельна вся строка)
        const tdName = document.createElement('td');
        const nameEl = document.createElement('span');
        nameEl.className = 'player-name-link';

        nameEl.textContent = getLocalePlayerName(p.name);

        nameEl.style.color = pickRaceColor(p.races);           // ← цвет расы
        nameEl.style.setProperty('--race-color', pickRaceColor(p.races)); // ← для hover
        tdName.appendChild(nameEl);
        // Локализованная приписка «(вы)» для своей строки
        if (state.user?.player_id && p.id === state.user.player_id) {
            const me = document.createElement('span');
            me.className = 'lb-me-suffix';
            me.textContent = t('players.me_suffix');
            tdName.appendChild(me);
        }
        tr.appendChild(tdName);

        // Вся строка открывает страницу игрока (навигацию делает onOpenPlayer)
        tr.style.cursor = 'pointer';
        tr.addEventListener('click', () => {
            cb.onOpenPlayer(p.id, p.name);
        });

        // Elo
        // const tdElo = document.createElement('td');
        // tdElo.className = 'elo-cell';
        // tdElo.textContent = p.elo > 0 ? String(p.elo) : '—';
        // // Подсветим топ-3
        // if (p.elo >= 1600) tdElo.classList.add('elo-cell--high');
        // else if (p.elo >= 1550) tdElo.classList.add('elo-cell--mid');
        // tr.appendChild(tdElo);

        // Расы
        // Расы (скрывается на мобиле)
        const tdRaces = fmtRaceCells(p.races);
        tdRaces.classList.add('col-mobile-hide');
        tr.appendChild(tdRaces);

        // Активность
        const tdActivity = document.createElement('td');
        tdActivity.className = 'col-desktop activity-cell';
        tdActivity.textContent = p.activity_score > 0 ? p.activity_score.toFixed(1) : '—';
        tr.appendChild(tdActivity);

        // Статы — в порядке STAT_ORDER
        for (const axis of STAT_ORDER) {
            const td = document.createElement('td');
            td.className = 'col-desktop';
            td.appendChild(statBarCell(p[axis.key], axis.color));

            // Мобильная подпись к полоске (видна только на телефоне,
            // где шапка таблицы скрыта)
            const label = document.createElement('span');
            label.className = 'stat-mobile-label';
            label.dataset.i18n = axis.langKey;
            label.textContent = t(axis.langKey as TranslationKey);
            td.insertBefore(label, td.firstChild);

            tr.appendChild(td);
        }


        // Итого — сумма всех средних
        // const tdTotal = document.createElement('td');
        // tdTotal.className = 'total-cell col-mobile-hide';

        // const total = STAT_ORDER.reduce((sum, axis) => {
        //     const v = p[axis.key];
        //     return sum + (typeof v === 'number' ? v : 0);
        // }, 0);

        // Если у игрока вообще нет оценок — показываем «—»
        // const hasAny = STAT_ORDER.some((axis) => typeof p[axis.key] === 'number');
        // tdTotal.textContent = hasAny ? total.toFixed(2) : '—';

        // tr.appendChild(tdTotal);

        // Проверяем, модератор ли текущий пользователь
        // const canDelete = Boolean(
        //     state.user?.is_admin
        // );

        // В row добавляем ячейку с кнопкой
        // const tdActions = document.createElement('td');
        // tdActions.className = 'players-actions-cell';

        // if (canDelete) {
        //     const delBtn = document.createElement('button');
        //     delBtn.type = 'button';
        //     delBtn.className = 'player-delete-btn';
        //     delBtn.title = 'Удалить игрока';
        //     delBtn.textContent = '🗑';
        //     delBtn.addEventListener('click', (e) => {
        //         e.stopPropagation();   // не открывать карточку
        //         void (async () => {
        //             const confirmed = confirm(
        //                 `Удалить игрока "${p.name}"?\n\n` +
        //                 `Все оценки (${p.vote_count}) будут удалены безвозвратно.`
        //             );
        //             if (!confirmed) return;

        //             try {
        //                 await apiRequest(`/api/players/${p.id}`, {
        //                     method: 'DELETE',
        //                     token: state.token,
        //                 });
        //                 // Перезагружаем список
        //                 const { players } = await apiRequest<PlayersListResponse>('/api/players');
        //                 cachedPlayers = players;
        //                 renderPlayersTable(
        //                     sortPlayers(cachedPlayers, sortKey, sortDirection),
        //                     cb
        //                 );
        //             } catch (err) {
        //                 alert('Не удалось удалить: ' +
        //                     (err instanceof Error ? err.message : String(err)));
        //             }
        //         })();
        //     });
        //     tdActions.appendChild(delBtn);
        // }

        // tr.appendChild(tdActions);

        tbody.appendChild(tr);
    }
}

export function bindCreatePlayer(onCreated: () => void): void {
    const form = document.getElementById('form-create-player') as HTMLFormElement | null;
    if (!form) return;

    // Скрываем форму для не-модераторов
    if (!state.user?.is_moderator) {
        form.style.display = 'none';
        return;
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!state.token) {
            alert('Нужно войти, чтобы добавлять игроков');
            return;
        }
        const fd = new FormData(form);
        const name = String(fd.get('name') ?? '').trim();
        if (!name) return;

        try {
            await apiRequest('/api/players', {
                method: 'POST',
                token: state.token,
                body: { name },
            });
            form.reset();
            onCreated();
        } catch (err) {
            alert('Не удалось добавить игрока: ' +
                (err instanceof Error ? err.message : String(err)));
        }
    });
}

/**
 * Считает «итого» — сумму всех ненулевых параметров.
 * Если оценок нет — возвращает null, чтобы такие игроки уходили в конец.
 */
function calcTotal(p: PlayerWithStats): number | null {
    let sum = 0;
    let hasAny = false;
    for (const axis of STAT_ORDER) {
        const v = p[axis.key];
        if (typeof v === 'number') {
            sum += v;
            hasAny = true;
        }
    }
    return hasAny ? sum : null;
}

type SimpleSortKey = Exclude<SortKey, 'races'>;

function getSortValue(p: PlayerWithStats, key: SimpleSortKey): string | number | null {
    if (key === 'name') return p.name.toLowerCase();
    if (key === 'total') return calcTotal(p);
    if (key === 'tier') return p.tier;
    if (key === 'elo') return p.elo;
    if (key === 'activity') return p.activity_score;
    return p[key];
}

/**
 * Считает частоту каждой расы среди всех игроков.
 * Возвращает карту «раса → сколько игроков её имеют».
 */
function countRacePopularity(
    players: PlayerWithStats[]
): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const p of players) {
        for (const r of p.races) {
            counts[r] = (counts[r] ?? 0) + 1;
        }
    }
    return counts;
}

/**
 * Возвращает «ключевую расу» игрока — самую популярную из его рас.
 * Используется для группировки при сортировке по расам.
 */
function primaryRace(
    p: PlayerWithStats,
    popularity: Record<string, number>
): string {
    if (p.races.length === 0) return 'zzz-empty'; // пустые — в самый конец
    // Берём ту расу игрока, у которой выше популярность
    return [...p.races].sort(
        (a, b) => (popularity[b] ?? 0) - (popularity[a] ?? 0)
    )[0] ?? 'zzz-empty';
}

/**
 * Сортирует список игроков.
 * Особый случай: 'races' — группировка по популярности рас,
 * внутри группы — алфавит по имени.
 */
function sortPlayers(
    players: PlayerWithStats[],
    key: SortKey,
    dir: SortDirection
): PlayerWithStats[] {
    // --- Сортировка по расам: своя логика ---
    if (key === 'races') {
        const popularity = countRacePopularity(players);

        // Порядок рас: сначала по популярности, потом алфавит для стабильности
        const raceOrder = Object.keys(popularity).sort((a, b) => {
            const diff = (popularity[b] ?? 0) - (popularity[a] ?? 0);
            return diff !== 0 ? diff : a.localeCompare(b);
        });

        // Если сортировка по возрастанию — переворачиваем порядок рас
        const orderedRaces = dir === 'asc' ? raceOrder : [...raceOrder].reverse();

        const raceIndex: Record<string, number> = {};
        orderedRaces.forEach((r, i) => {
            raceIndex[r] = i;
        });

        return [...players].sort((a, b) => {
            const ra = primaryRace(a, popularity);
            const rb = primaryRace(b, popularity);

            const ia = raceIndex[ra] ?? 999;
            const ib = raceIndex[rb] ?? 999;

            // Сначала по индексу расы
            if (ia !== ib) return ia - ib;

            // Внутри одной расы — алфавит по имени (всегда по возрастанию)
            return a.name.localeCompare(b.name, 'ru');
        });
    }

    // --- Обычная сортировка ---
    const mult = dir === 'asc' ? 1 : -1;
    return [...players].sort((a, b) => {
        const av = getSortValue(a, key);
        const bv = getSortValue(b, key);

        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;

        if (typeof av === 'string' && typeof bv === 'string') {
            return av.localeCompare(bv, 'ru') * mult;
        }

        return (Number(av) - Number(bv)) * mult;
    });
}

/**
 * Навешивает обработчики клика на заголовки таблицы.
 * Вызывается один раз после первой загрузки.
 * Идемпотентна — повторный вызов не создаёт дублей.
 */
let sortingBound = false;
function bindSorting(cb: PlayersCallbacks): void {
    if (sortingBound) return;
    sortingBound = true;

    const table = document.querySelector('.players-table');
    if (!table) return;

    const headers = table.querySelectorAll<HTMLTableCellElement>('th[data-sort-key]');
    headers.forEach((th) => {
        th.addEventListener('click', () => {
            const key = th.dataset.sortKey as SortKey | undefined;
            if (!key) return;

            if (sortKey === key) {
                sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                sortKey = key;
                sortDirection = 'desc';
            }

            const sorted = sortPlayers(cachedPlayers, sortKey, sortDirection);
            renderPlayersTable(sorted, cb);
            updateSortIndicators();
        });
    });

    updateSortIndicators();
}

/**
 * Обновляет стрелки ▲ / ▼ в заголовках.
 */
function updateSortIndicators(): void {
    const table = document.querySelector('.players-table');
    if (!table) return;

    table.querySelectorAll<HTMLTableCellElement>('th[data-sort-key]').forEach((th) => {
        const key = th.dataset.sortKey as SortKey | undefined;
        const label = th.dataset.label ?? th.textContent ?? '';

        // Убираем старую стрелку, если была
        const cleanLabel = label.replace(/\s*[▲▼]$/, '');
        th.textContent = cleanLabel;

        if (key === sortKey) {
            const arrow = sortDirection === 'asc' ? '▲' : '▼';
            th.textContent = `${cleanLabel} ${arrow}`;
            th.classList.add('is-sorted');
            th.classList.toggle('is-sorted-asc', sortDirection === 'asc');
            th.classList.toggle('is-sorted-desc', sortDirection === 'desc');
        } else {
            th.classList.remove('is-sorted', 'is-sorted-asc', 'is-sorted-desc');
        }
    });
}

// ============================================================
// Контролы сортировки для вида «карточки»
// ============================================================

/**
 * Заполняет выпадающий список ключей сортировки и синхронизирует
 * индикатор направления с текущим состоянием.
 */
function refreshWallSortUI(): void {
    const select = document.getElementById('players-sort-key') as HTMLSelectElement | null;
    const dirBtn = document.getElementById('players-sort-dir') as HTMLButtonElement | null;

    if (select) {
        select.innerHTML = '';
        for (const key of WALL_SORT_KEYS) {
            const opt = document.createElement('option');
            opt.value = key;
            opt.textContent = sortKeyLabel(key);
            select.appendChild(opt);
        }
        select.value = sortKey;
    }

    if (dirBtn) {
        dirBtn.textContent = sortDirection === 'asc' ? '▲' : '▼';
        const label = sortDirection === 'asc' ? t('players.sort_asc') : t('players.sort_desc');
        dirBtn.title = label;
        dirBtn.setAttribute('aria-label', label);
    }
}

/**
 * Навешивает обработчики на контролы сортировки вида «карточки».
 * Идемпотентна — повторный вызов не создаёт дублей.
 */
let wallSortBound = false;
function bindWallSort(cb: PlayersCallbacks): void {
    if (wallSortBound) return;

    const select = document.getElementById('players-sort-key') as HTMLSelectElement | null;
    const dirBtn = document.getElementById('players-sort-dir') as HTMLButtonElement | null;
    if (!select || !dirBtn) return;

    wallSortBound = true;
    refreshWallSortUI();

    const applySort = (): void => {
        updateSortIndicators();
        refreshWallSortUI();
        if (currentView === 'wall') {
            renderWall(cachedPlayers);
        } else {
            renderPlayersTable(sortPlayers(cachedPlayers, sortKey, sortDirection), cb);
        }
    };

    select.addEventListener('change', () => {
        sortKey = (select.value as SortKey) || 'name';
        sortDirection = 'desc';
        applySort();
    });

    dirBtn.addEventListener('click', () => {
        sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        applySort();
    });
}

// ============================================================
// Вид «Стили игры» (стена)
// ============================================================

type ViewMode = 'table' | 'wall' | 'tierlist';

let currentView: ViewMode = 'table';

let searchQuery = '';

/**
 * Строит одну плитку игрока для стены.
 */
function buildTile(player: PlayerWithStats): HTMLAnchorElement {
    const race = dominantRace(player.races);
    const color = pickRaceColor(player.races);

    const tile = document.createElement('a');
    tile.className = 'player-tile';
    tile.href = `/?player=${encodeURIComponent(player.name)}`;
    tile.style.setProperty('--race-color', color);

    // Шапка: иконка + ник
    const header = document.createElement('div');
    header.className = 'player-tile-header';

    if (race !== 'MIXED') {
        const img = document.createElement('img');
        img.src = RACE_ICON_URL[race];
        img.alt = RACE_LABELS[race];
        img.className = 'player-tile-icon';
        header.appendChild(img);
    }

    const name = document.createElement('span');
    name.className = 'player-tile-name';
    name.textContent = getLocalePlayerName(player.name);
    header.appendChild(name);

    tile.appendChild(header);

    // Радар
    const radarWrap = document.createElement('div');
    radarWrap.className = 'player-tile-radar';
    radarWrap.innerHTML = buildRadarSVG({
        stats: player,
        color,
        size: 330,
        uniqueId: `player-${player.id}`,
        showVertices: false,
    });
    tile.appendChild(radarWrap);

    // Подвал: голоса + итого
    const footer = document.createElement('div');
    footer.className = 'player-tile-footer';

    const total = calcTotal(player);

    const votes = document.createElement('span');
    // votes.dataset.i18n = "player.votes";
    votes.innerHTML = `${t('player.votes')}: <strong>${player.vote_count}</strong>`;

    const totalEl = document.createElement('span');
    // totalEl.dataset.i18n = "player.total";
    totalEl.innerHTML = `${t('player.total')}: <strong>${total !== null ? total.toFixed(2) : '—'}</strong>`;

    footer.append(votes, totalEl);
    tile.appendChild(footer);

    return tile;
}

/**
 * Рендерит стену из массива игроков.
 */
function renderWall(_players: PlayerWithStats[]): void {
    const wall = document.getElementById('players-wall');
    if (!wall) return;

    const filtered = searchQuery
        ? _players.filter((p) =>
            p.name.toLowerCase().includes(searchQuery) ||
            (p.aka ?? '').toLowerCase().includes(searchQuery)
        )
        : _players;


    wall.innerHTML = '';

    refreshWallSortUI();

    if (filtered.length === 0) {
        wall.innerHTML = searchQuery
            ? `<p class="hint">${t('players.nothing_found')}</p>`
            : `<p class="hint">${t('players.no_players')}</p>`;
        return;
    }
    // Сортируем по текущему выбранному ключу и направлению
    const sorted = sortPlayers(filtered, sortKey, sortDirection);

    for (const p of sorted) {
        wall.appendChild(buildTile(p));
    }
}

// ============================================================
// Вид «Тирлист» — игроки сгруппированы по тирам (S..F),
// игроки без тира — отдельной группой в самом низу.
// ============================================================

/** Компактная карточка игрока для тирлиста */
function buildTierListCard(player: PlayerWithStats): HTMLAnchorElement {
    const color = pickRaceColor(player.races);
    const race = dominantRace(player.races);

    const a = document.createElement('a');
    a.className = 'tierlist-card';
    a.href = `/?player=${encodeURIComponent(player.name)}`;
    a.style.setProperty('--race-color', color);

    if (race !== 'MIXED') {
        const img = document.createElement('img');
        img.src = RACE_ICON_URL[race];
        img.alt = RACE_LABELS[race];
        img.className = 'tierlist-card-icon';
        a.appendChild(img);
    } else {
        const mixed = document.createElement('span');
        mixed.className = 'tierlist-card-icon tierlist-card-icon--mixed';
        mixed.textContent = '◆';
        a.appendChild(mixed);
    }

    const body = document.createElement('div');
    body.className = 'tierlist-card-body';

    const name = document.createElement('span');
    name.className = 'tierlist-card-name';
    name.textContent = getLocalePlayerName(player.name);
    name.style.color = color;
    body.appendChild(name);

    const meta = document.createElement('span');
    meta.className = 'tierlist-card-meta';
    const mmr = Number.isFinite(player.elo) ? player.elo : 1500;
    const activity = player.activity_score > 0 ? player.activity_score.toFixed(1) : '—';
    meta.textContent = `MMR ${mmr} · ${t('leaderboard.activity')} ${activity}`;
    body.appendChild(meta);

    a.appendChild(body);
    return a;
}

/** Переключатель режима тирлиста: GHOST / Средняя / Моя + выбор любого игрока. */
function buildTierListControls(host: HTMLElement, players: PlayerWithStats[]): void {
    const modes: { mode: TierListMode; key: TranslationKey }[] = [
        { mode: 'ghost', key: 'player.card_mode_ghost' },
        { mode: 'average', key: 'player.card_mode_average' },
        { mode: 'personal', key: 'player.card_mode_mine' },
    ];

    const isAuthed = Boolean(state.token && state.user);
    const active = effectiveTierListMode();

    const bar = document.createElement('div');
    bar.className = 'tierlist-modes';

    for (const item of modes) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `tierlist-mode-btn${active === item.mode ? ' is-active' : ''}`;
        btn.textContent = t(item.key);

        if (item.mode === 'personal' && !isAuthed) {
            btn.disabled = true;
            btn.title = t('player.login_to_rate');
        }

        btn.addEventListener('click', () => {
            if (tierlistMode === item.mode) return;
            tierlistMode = item.mode;
            localStorage.setItem(TIERLIST_MODE_STORAGE, item.mode);
            renderTierList(players);
        });

        bar.appendChild(btn);
    }

    // Выпадающий список: посмотреть тирлист любого пользователя, выставлявшего тиры.
    const raterWrap = document.createElement('label');
    raterWrap.className = 'tierlist-rater';

    const raterLabel = document.createElement('span');
    raterLabel.className = 'tierlist-rater-label';
    raterLabel.textContent = t('players.tierlist_rater_label');
    raterWrap.appendChild(raterLabel);

    const select = document.createElement('select');
    select.className = 'tierlist-rater-select';
    select.classList.toggle('is-active', active === 'user');
    select.disabled = tierRaters !== null && tierRaters.length === 0;

    const placeholder = new Option(t('players.tierlist_rater_placeholder'), '');
    select.appendChild(placeholder);

    for (const r of tierRaters ?? []) {
        const suffix =
            r.player_name && r.player_name !== r.username ? ` · ${r.player_name}` : '';
        select.appendChild(new Option(`${r.username}${suffix} (${r.count})`, r.user_id));
    }
    select.value = active === 'user' ? (tierlistRaterId ?? '') : '';

    select.addEventListener('change', () => {
        const value = select.value;

        // Пустое значение — выходим из режима чужого тирлиста.
        if (!value) {
            tierlistRaterId = null;
            localStorage.removeItem(TIERLIST_RATER_STORAGE);
            if (tierlistMode === 'user') {
                tierlistMode = 'ghost';
                localStorage.setItem(TIERLIST_MODE_STORAGE, 'ghost');
            }
            renderTierList(players);
            return;
        }

        if (tierlistRaterId === value && tierlistMode === 'user') return;

        tierlistRaterId = value;
        tierlistMode = 'user';
        localStorage.setItem(TIERLIST_RATER_STORAGE, value);
        localStorage.setItem(TIERLIST_MODE_STORAGE, 'user');

        // Сбрасываем кэш тиров — renderTierList догрузит их для нового пользователя.
        raterTiersRequest++;
        raterTiers = null;
        raterTiersUserId = null;
        raterTiersLoadingFor = null;
        raterTiersError = false;
        renderTierList(players);
    });

    raterWrap.appendChild(select);
    bar.appendChild(raterWrap);

    host.appendChild(bar);
}

function renderTierList(_players: PlayerWithStats[]): void {
    const host = document.getElementById('players-tierlist-view');
    if (!host) return;
    host.innerHTML = '';

    // Ленивая догрузка своих тиров для режима «Моя», затем перерисовка.
    if (tierlistMode === 'personal' && state.token && myTiers === null) {
        void ensureMyTiers().then(() => {
            if (currentView === 'tierlist') renderTierList(cachedPlayers);
        });
    }

    // Список пользователей с тирами — для выпадающего списка «Тирлист игрока».
    if (tierRaters === null && !tierRatersLoading) {
        void ensureTierRaters().then(() => {
            if (currentView === 'tierlist') renderTierList(cachedPlayers);
        });
    }

    buildTierListControls(host, _players);

    const mode = effectiveTierListMode();

    // Чужой тирлист: пока тиры не загружены — заглушка вместо пустых групп.
    if (mode === 'user' && tierlistRaterId) {
        const ready = raterTiers !== null && raterTiersUserId === tierlistRaterId;
        if (!ready) {
            // Перерисовку планируем только при старте загрузки. Если запрос уже
            // идёт, его собственный .then перерисует список — иначе получаем
            // бесконечный цикл синхронных перерисовок и вкладка зависает.
            if (raterTiersLoadingFor !== tierlistRaterId) {
                void ensureRaterTiers(tierlistRaterId).then(() => {
                    if (currentView === 'tierlist') renderTierList(cachedPlayers);
                });
            }
            const loading = document.createElement('p');
            loading.className = 'hint';
            loading.textContent = t('common.loading');
            host.appendChild(loading);
            return;
        }
        if (raterTiersError) {
            const error = document.createElement('p');
            error.className = 'hint';
            error.textContent = t('players.tierlist_rater_error');
            host.appendChild(error);
            return;
        }
        if ((raterTiers?.size ?? 0) === 0) {
            const empty = document.createElement('p');
            empty.className = 'hint';
            empty.textContent = t('players.tierlist_rater_empty');
            host.appendChild(empty);
            return;
        }
    }

    const filtered = searchQuery
        ? _players.filter((p) =>
            p.name.toLowerCase().includes(searchQuery) ||
            (p.aka ?? '').toLowerCase().includes(searchQuery)
        )
        : _players;

    if (filtered.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'hint';
        empty.textContent = searchQuery ? t('players.nothing_found') : t('players.no_players');
        host.appendChild(empty);
        return;
    }

    // Группировка по округлённому тиру выбранного режима: 7..1 (S..F) + «без тира».
    const groups = new Map<number | 'none', PlayerWithStats[]>();
    for (const p of filtered) {
        const value = tierForPlayer(p, mode);
        const key: number | 'none' = tierLetter(value) !== null
            ? Math.max(1, Math.min(7, Math.round(value as number)))
            : 'none';
        const arr = groups.get(key);
        if (arr) arr.push(p);
        else groups.set(key, [p]);
    }

    const container = document.createElement('div');
    container.className = 'tierlist';

    const renderGroup = (key: number | 'none', players: PlayerWithStats[]): void => {
        const group = document.createElement('div');
        group.className = 'tierlist-group';
        if (key === 'none') group.classList.add('tierlist-group--none');

        const head = document.createElement('div');
        head.className = 'tierlist-head';

        if (key === 'none') {
            const label = document.createElement('span');
            label.className = 'tierlist-head-label';
            label.textContent = t('players.tierlist_no_tier');
            head.appendChild(label);
        } else {
            const badge = document.createElement('span');
            badge.className = 'tier-badge tierlist-head-badge';
            badge.dataset.tier = String(key);
            badge.textContent = tierLetter(key) ?? '?';
            head.appendChild(badge);

            const label = document.createElement('span');
            label.className = 'tierlist-head-label';
            label.textContent = t('player.tier_title');
            head.appendChild(label);
        }

        const count = document.createElement('span');
        count.className = 'tierlist-head-count';
        count.textContent = String(players.length);
        head.appendChild(count);
        group.appendChild(head);

        const list = document.createElement('div');
        list.className = 'tierlist-players';
        const sorted = [...players].sort((a, b) => b.activity_score - a.activity_score);
        for (const p of sorted) list.appendChild(buildTierListCard(p));
        group.appendChild(list);

        container.appendChild(group);
    };

    for (let lvl = 7; lvl >= 1; lvl--) {
        const players = groups.get(lvl);
        if (players && players.length) renderGroup(lvl, players);
    }
    const none = groups.get('none');
    if (none && none.length) renderGroup('none', none);

    host.appendChild(container);
}

let lastView: ViewMode | null = null;
let lastViewCb: PlayersCallbacks | null = null;

// ============================================================
// Зал славы — лучший и худший игрок по каждому параметру
// ============================================================

/** Минимум оценок, чтобы игрок попал в зал славы */
const HALL_MIN_VOTES = 2;

/** Описательные подписи «максимум/минимум» для каждого параметра */
const HALL_LABELS: Record<StatKey, { high: TranslationKey; low: TranslationKey }> = {
    adaptiveness: { high: 'hall.adaptiveness.high', low: 'hall.adaptiveness.low' },
    aggression: { high: 'hall.aggression.high', low: 'hall.aggression.low' },
    turtle: { high: 'hall.turtle.high', low: 'hall.turtle.low' },
    variety: { high: 'hall.variety.high', low: 'hall.variety.low' },
    survival: { high: 'hall.survival.high', low: 'hall.survival.low' },
    greed: { high: 'hall.greed.high', low: 'hall.greed.low' },
};

/** Лидер по оси: high=true — максимум, иначе минимум */
function hallLeader(
    axis: StatAxis,
    players: PlayerWithStats[],
    high: boolean
): { player: PlayerWithStats; value: number } | null {
    let best: PlayerWithStats | null = null;
    let bestVal: number | null = null;
    for (const p of players) {
        const v = p[axis.key];
        if (typeof v !== 'number') continue;
        if (bestVal === null || (high ? v > bestVal : v < bestVal)) {
            bestVal = v;
            best = p;
        }
    }
    return best && bestVal !== null ? { player: best, value: bestVal } : null;
}

/** Одна запись зала славы: подпись + ник + значение */
function buildHallEntry(
    player: PlayerWithStats,
    _axis: StatAxis,
    value: number,
    label: TranslationKey,
    kind: 'high' | 'low'
): HTMLElement {
    const color = pickRaceColor(player.races);
    const entry = document.createElement('a');
    entry.className = `hall-entry hall-entry--${kind}`;
    entry.href = `/?player=${encodeURIComponent(player.name)}`;
    entry.style.setProperty('--race-color', color);

    const cap = document.createElement('span');
    cap.className = 'hall-entry-cap';
    cap.textContent = t(label);

    const name = document.createElement('span');
    name.className = 'hall-entry-name';
    name.textContent = getLocalePlayerName(player.name);
    name.style.color = color;

    const val = document.createElement('span');
    val.className = 'hall-entry-value';
    val.textContent = value.toFixed(2);

    entry.append(cap, name, val);
    return entry;
}

/** Рендерит панель зала славы справа от списка игроков */
function renderHall(players: PlayerWithStats[]): void {
    const host = document.getElementById('players-hall');
    if (!host) return;
    host.innerHTML = '';

    const title = document.createElement('h3');
    title.className = 'hall-title';
    title.textContent = t('hall.title');
    host.appendChild(title);

    // Учитываем только игроков с достаточным числом оценок
    const eligible = players.filter((p) => p.vote_count >= HALL_MIN_VOTES);
    if (eligible.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'hall-empty hint';
        empty.textContent = t('hall.empty');
        host.appendChild(empty);
        return;
    }

    const sub = document.createElement('p');
    sub.className = 'hall-subtitle';
    sub.textContent = t('hall.subtitle');
    host.appendChild(sub);

    for (const axis of STAT_ORDER) {
        const high = hallLeader(axis, eligible, true);
        const low = hallLeader(axis, eligible, false);
        if (!high || !low) continue;

        const row = document.createElement('div');
        row.className = 'hall-row';
        row.style.setProperty('--stat-color', axis.color);

        const head = document.createElement('div');
        head.className = 'hall-row-head';
        head.textContent = t(axis.langKey);
        head.style.color = axis.color;
        row.appendChild(head);

        row.appendChild(buildHallEntry(high.player, axis, high.value, HALL_LABELS[axis.key].high, 'high'));
        row.appendChild(buildHallEntry(low.player, axis, low.value, HALL_LABELS[axis.key].low, 'low'));

        host.appendChild(row);
    }
}

/**
 * Переключает вид: table ↔ wall.
 */
function setView(view: ViewMode, cb: PlayersCallbacks): void {
    currentView = view;

    lastView = view;
    lastViewCb = cb;

    const tableView = document.getElementById('players-table-view');
    const wallView = document.getElementById('players-wall-view');
    const tierView = document.getElementById('players-tierlist-view');
    if (!tableView || !wallView || !tierView) return;

    tableView.classList.toggle('hidden', view !== 'table');
    wallView.classList.toggle('hidden', view !== 'wall');
    tierView.classList.toggle('hidden', view !== 'tierlist');

    if (view === 'table') {
        renderPlayersTable(
            sortPlayers(cachedPlayers, sortKey, sortDirection),
            cb
        );
        updateSortIndicators();
    } else if (view === 'wall') {
        renderWall(cachedPlayers);
    } else {
        renderTierList(cachedPlayers);
    }

    // Обновляем активную кнопку
    document.querySelectorAll<HTMLButtonElement>('.view-toggle-btn').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.view === view);
    });

    // Зал славы строится по полному списку и не зависит от вида
    renderHall(cachedPlayers);

    localStorage.setItem('playersView', view);
}

/**
 * Навешивает обработчики на переключатель видов.
 */
let viewToggleBound = false;

function bindViewToggle(cb: PlayersCallbacks): void {
    if (viewToggleBound) return;

    const toggle = document.getElementById('view-toggle');
    if (!toggle) {
        console.warn('[players] #view-toggle not found');
        return;
    }

    toggle.addEventListener('click', (e) => {
        const target = (e.target as HTMLElement).closest('.view-toggle-btn') as HTMLElement | null;
        if (!target) return;

        const view = target.dataset.view as ViewMode | undefined;
        if (view === 'table' || view === 'wall' || view === 'tierlist') {
            setView(view, cb);
        }
    });

    viewToggleBound = true;
}

/**
 * Ищет игрока в кэше по имени.
 * Возвращает объект или null, если кэш пуст или игрок не найден.
 */
export function findPlayerByName(name: string): { id: number; name: string } | null {
    const lower = name.toLowerCase();
    const found = cachedPlayers.find((p) => p.name.toLowerCase() === lower);
    return found ? { id: found.id, name: found.name } : null;
}
